import { tintOf } from '../lib/theme.js'

/**
 * The app's coloured glyph tile: a small rounded square, washed in a colour, with an icon
 * drawn on it in the saturated version of that same colour.
 *
 * It exists in one place because it had grown in several — the settings sidebar and the
 * mode cards on Configure each had their own box, their own size, their own custom property
 * and their own idea of what the glyph should look like, so restyling "the icon tiles" meant
 * finding all of them and hoping none had been missed. Everything about how a tile LOOKS
 * lives here and in the single `.glyph-tile` rule; call sites supply only what is particular
 * to them — which glyph, which colour, how big.
 *
 * The wash is derived from the tint unless one is passed. Deriving it means a new tile needs
 * one colour rather than a matched pair somebody has to eyeball, and passing one covers the
 * palette that was chosen by hand rather than computed (see TILE in lib/tileLook.js).
 */
export default function GlyphTile({
  icon: Glyph,
  tint,
  bg,
  size = 26,
  // Roughly five-eighths of the box at every size, which is the ratio the sidebar's 16-on-26
  // already used — worth keeping as a rule so a bigger tile does not end up with a glyph
  // rattling around inside it.
  glyphSize = Math.round(size * 0.615),
  // Drawn grey rather than in its colour: used for a thing that is switched off, where the
  // colour would claim it is live.
  muted = false,
  strokeWidth = 2,
  className = '',
  ...rest
}) {
  return (
    <span
      className={`glyph-tile ${muted ? 'muted' : ''} ${className}`.trim()}
      style={{
        '--tint': tint,
        '--tile-bg': bg || (tint ? tintOf(tint, 0.86) : undefined),
        '--tile-size': `${size}px`,
      }}
      {...rest}
    >
      {Glyph && <Glyph size={glyphSize} strokeWidth={strokeWidth} />}
    </span>
  )
}
